/**
 * HELP WALKTHROUGHS — "Show me how", step by step, on real screens.
 *
 * Each walkthrough is a short run of steps. A step names a SHOT and a TARGET
 * in it (public/help-shots.js, generated by scripts/capture-help-shots.py from
 * the dev harnesses) and says, in a sentence or two, what to do there. The
 * viewer draws the screenshot, zooms towards the target and rings it.
 *
 * Copy here is the deliverable, like help-content.js: a rewording is a change
 * to Help, and it should look like one in a diff. When a SCREEN changes, re-run
 * the capture — the copy only needs touching when what you DO changes.
 *
 * Shape:
 *   { id, area, title, line, art, who?, go?: { label, view }, steps: [
 *       { shot, target?, title, text, tip?, who? } ] }
 * `who` gates a walkthrough or a step: 'calc' is the Calculate pilot; any
 * other value is a rail id ('invoices', 'enquiries', 'approvals'…) and follows
 * that screen's own role gate, so nobody is walked round a screen they cannot
 * open. `go.view` is the rail id the last step's button opens; `url` is what
 * the frame's address bar shows when it is not just the view.
 */

export const AREAS = [
  // Calculate first for the people who have it (a pilot learning a new tool);
  // then Track, in the order of the rail. An area shows only when one of its
  // walkthroughs is open to you.
  { id: 'calculate', label: 'Calculate', who: 'calc' },
  { id: 'home', label: 'Home' },
  { id: 'tasks', label: 'Tasks' },
  { id: 'time', label: 'Time' },
  { id: 'projects', label: 'Projects' },
  { id: 'mail', label: 'Mail' },
  { id: 'files', label: 'Files' },
  { id: 'calendar', label: 'Calendar' },
  { id: 'enquiries', label: 'Enquiries' },
  { id: 'clients', label: 'Clients' },
  { id: 'team', label: 'Team' },
  { id: 'finance', label: 'Finance' },
  { id: 'ask', label: 'Ask Track' },
];

/**
 * GROUPS (v4.0.0 refresh, p37) — the Help home's filter chips. Coarser than the
 * areas: each tile still wears its own area as an eyebrow ("Mail", "Files"), and
 * the chip filters by the group the area falls in. A group shows only when one
 * of its walkthroughs is open to you.
 */
export const GROUPS = [
  { id: 'start', label: 'Getting started', areas: ['home', 'tasks', 'calendar', 'team', 'ask'] },
  { id: 'projects', label: 'Projects', areas: ['projects'] },
  { id: 'time', label: 'Time', areas: ['time'] },
  { id: 'mailfiles', label: 'Mail and files', areas: ['mail', 'files'] },
  { id: 'money', label: 'Money', areas: ['enquiries', 'clients', 'finance'] },
  { id: 'calculate', label: 'Calculate', areas: ['calculate'], who: 'calc' },
];
export const groupOf = (area) => (GROUPS.find((g) => g.areas.includes(area)) || GROUPS[0]).id;

/** The hero on Help home: the walkthrough someone new should watch first. */
export const FIRST_MORNING = 'home-morning';

export const WALKTHROUGHS = [

  // ── Home ──────────────────────────────────────────────────────────────────
  // v4.0.0 (22/10/2026): Home is one list now — Needs you — so both Home
  // walkthroughs are drawn on the refreshed screen (shot t-home-v4).
  {
    id: 'home-morning', area: 'home', art: 'home',
    title: 'Your first morning in Track',
    line: 'What needs you, what the buttons do, and where everything else lives. It plays by itself; pause any time.',
    go: { label: 'Open Home', view: 'home' }, url: '',
    steps: [
      { shot: 't-home-v4', target: 'needs', title: 'What needs you',
        text: 'The number is how many things are waiting on you today. The line beside it says what they are, most urgent first.' },
      { shot: 't-home-v4', target: 'row', title: 'One row, one thing',
        text: 'Each row is one project or invoice: what kind of thing it is on the left — Late, Sign, Check, Money — then the project, who it is with and how long it has waited.' },
      { shot: 't-home-v4', target: 'verb', title: 'The button does it',
        text: 'The button on the right is the verb that clears the row — Check, Chase, Sign and issue. Anything that changes a record asks you to press twice.' },
      { shot: 't-home-v4', target: 'filter', title: 'Work or money', who: 'invoices',
        text: 'If you see money, All, Work and Money narrow the list to the jobs or to the invoices to chase.' },
      { shot: 't-home-v4', target: 'search', title: 'Search, or just type it',
        text: 'Four digits jump to a project. A sentence like “log 2h 8601” does it. [shortcut K] opens the same box from anywhere.' },
      { shot: 't-home-v4', target: 'week', title: 'Your week at a glance',
        text: 'On the right: deadlines in the next fortnight, the hours logged this week and how many sheets are in, and the latest project email.' },
    ],
  },
  {
    id: 'home-read', area: 'home', art: 'home',
    title: 'Work through what needs you',
    line: 'Your morning on Home: most urgent first, one button each.',
    go: { label: 'Open Home', view: 'home' }, url: '',
    steps: [
      { shot: 't-home-v4', target: 'needs', title: 'Needs you',
        text: 'Late work first, then what is ready to sign, what is waiting on your check, and invoices to chase. The count says how many.' },
      { shot: 't-home-v4', target: 'row', title: 'Read the row',
        text: 'The tag says what kind of thing it is; the line under the title says who it is with and how long it has been waiting. Orange means late.' },
      { shot: 't-home-v4', target: 'verb', title: 'Press the button',
        text: 'One button per row, and it does the thing: Check opens the work to check, Chase drafts the reminder, Sign and issue asks you to press again before it changes anything.' },
      { shot: 't-home-v4', target: 'list', title: 'Clear it top to bottom',
        text: 'A row leaves the list when its record moves on. Most mornings the list is clear in a few minutes.' },
      { shot: 't-home-v4', target: 'foot', title: 'Everything else',
        text: 'Done for today? Everything else lives on Projects and Tasks.' },
    ],
  },

  // ── Tasks ─────────────────────────────────────────────────────────────────
  {
    id: 'tasks-plan', area: 'tasks', art: 'tasks',
    title: 'Your day, and the team’s, in Tasks',
    line: 'Mine answers three questions; Team is the table for the 09:15 meeting.',
    go: { label: 'Open Tasks', view: 'tasks' },
    steps: [
      { shot: 't-tasks', target: 'tabs', title: 'Mine and Team',
        text: 'Tasks opens on Mine, your own work. Team shows everyone. The number beside the title is how many open tasks the view holds.' },
      { shot: 't-tasks', target: 'brief', title: 'Track this morning',
        text: 'One paragraph that names the records that need you: who is waiting on your check, what of yours is late, and who is holding your work. Click a blue name to open that task.' },
      { shot: 't-tasks', target: 'waiting', title: 'Waiting on you',
        text: 'Work other people have sent you to check, oldest first. The age turns orange at two days. Check (or Review) opens it.' },
      { shot: 't-tasks', target: 'list', title: 'Your list',
        text: 'Your own open tasks, grouped Overdue, Today, This week, Later and No date. Each row shows the project and instruction, such as 8536 · I2, and a chip when it was raised from an email, a client card or an enquiry.' },
      { shot: 't-tasks-lower', target: 'yourewaiting', title: 'You’re waiting on',
        text: 'Your tasks held by someone else, longest first: who, what for, how long (orange at seven days) and when it was last chased. Chase or Nudge records it straight away and turns to Chased or Nudged.' },
      { shot: 't-tasks-team', target: 'table', title: 'Team, for 09:15',
        text: 'One row per person: open, overdue, what they are waiting on and for how long, checks they owe others, and whether they are in, on site or on leave. Click a person to see their tasks. Unassigned is last, with Assign on each task.' },
    ],
  },
  {
    id: 'tasks-add', area: 'tasks', art: 'tasks',
    title: 'Add a task, move it, finish it',
    line: 'Type it on your list, or use the form; then the panel moves it on.',
    go: { label: 'Open Tasks', view: 'tasks' },
    steps: [
      { shot: 't-tasks', target: 'add', title: 'Type it on your list',
        text: 'The line at the top of Your list adds a task for you. Put a four-digit project number in the text to file it to that project, then press Enter. N jumps to this line from anywhere on the page.' },
      { shot: 't-tasks', target: 'new', title: 'Or use the form',
        text: 'The orange + opens New task, for work you are giving to someone else.' },
      { shot: 't-tasks-new', target: 'project', title: 'Which project',
        text: 'Pick the project. Leave it empty for overhead work — that can never be billed.' },
      { shot: 't-tasks-new', target: 'who', title: 'Who does it, who checks it',
        text: 'The person it goes to, and a checker if it needs one. Nobody yet sends it to Unassigned for the team meeting.' },
      { shot: 't-tasks-new', target: 'due', title: 'When',
        text: 'A due date puts it in the right group on the person’s list.' },
      { shot: 't-tasks', target: 'check', title: 'Tick it off',
        text: 'The ring on each row marks a task done. It stays on your list, struck through, until the end of the day.' },
      { shot: 't-tasks-panel', target: 'actions', title: 'The panel moves it on',
        text: 'Click a task to open it on the right. The buttons depend on where it stands: Mark done, Waiting on…, Send for check or Reassign when it is open; Chase or It’s arrived when it is waiting; Approve or Return with comments when it is with you to check; Nudge or Take back when it is with someone else.' },
      { shot: 't-tasks-panel', target: 'time', title: 'Time goes on the timesheet',
        text: 'Tasks no longer run a timer. The link under the buttons opens the timesheet to log time against the project and instruction.' },
      { shot: 't-ask-task', target: 'field', title: 'Or type it in one line',
        text: '“Check beam calc 8601 @rajan fri” in the Ask Track bar’s Task mode reads the project, the person and the day for you.' },
    ],
  },

  // ── Time ──────────────────────────────────────────────────────────────────
  {
    id: 'time-log', area: 'time', art: 'time',
    title: 'Fill in and submit your timesheet',
    line: 'A day at a time or the whole week.',
    go: { label: 'Open Time', view: 'time' },
    steps: [
      { shot: 't-time', target: 'total', title: 'Your week',
        text: 'The week total, and how far short of the expected hours you are. Leave and bank holidays are already taken off.' },
      { shot: 't-time', target: 'day', title: 'Click a day to log it',
        text: 'Each cell is one project on one day. Click it to put hours in.' },
      { shot: 't-time-day', target: 'hours', title: 'Hours, the quick way',
        text: 'Type the hours, or use +0:15, +0:30, +1:00 and 7:30. Each row is per instruction, so the time lands on the work that was actually instructed.' },
      { shot: 't-time-day', target: 'note', title: 'Say what was done',
        text: 'A line about the day. It is what the client sees if the time is ever queried.' },
      { shot: 't-time-day', target: 'review', title: 'Review time',
        text: 'Tick this when you were checking someone else’s work. When the sheet is approved, the project moves to Ready to Issue.' },
      { shot: 't-time-day', target: 'extras', title: 'Mileage and expenses',
        text: 'Add a journey or a purchase from the same place. Both attach to this project and go to Finance with your sheet.' },
      { shot: 't-time', target: 'week', title: 'Or log the whole week',
        text: 'Click a project’s name to fill all five days at once.' },
      { shot: 't-time', target: 'add', title: 'Add a project',
        text: 'Search for a job, or pick ASP time — admin, meetings, training. A job with more than one instruction asks which.' },
      { shot: 't-time', target: 'submit', title: 'Submit by Tuesday 10:00',
        text: 'Last week locks at 10:00 on Tuesday. After that you can only change it by asking for it to be unlocked.',
        tip: 'Working the weekend? “Request weekend” asks for Saturday and Sunday to be opened first.' },
    ],
  },
  {
    id: 'time-approve', area: 'time', art: 'time', who: 'approvals',
    title: 'Approve the team’s timesheets',
    line: 'Approve, send back, or open a weekend.',
    go: { label: 'Open Approvals', view: 'approvals' }, url: 'time/approvals',
    steps: [
      { shot: 't-approvals', target: 'anchor', title: 'How many are waiting',
        text: 'The number of sheets waiting on you this week, and how many people have not filed at all.' },
      { shot: 't-approvals', target: 'brief', title: 'The brief',
        text: 'Track’s read of the week: total hours, who is missing, and anything worth a second look.' },
      { shot: 't-approvals', target: 'figures', title: 'The team’s week, day by day',
        text: 'Hours per day across everyone. Weekend time is orange, and Track warns when someone logged it without the weekend being opened.' },
      { shot: 't-approvals-cards', target: 'weekend', title: 'Weekend requests',
        text: 'Open the weekend for someone who asked, or decline it. Their Saturday time can only be approved once it is open.' },
      { shot: 't-approvals-cards', target: 'approve', title: 'Approve or send back',
        text: 'Approve a sheet from its card, or send it back with a note. Click the card itself for the full sheet, its history and the billable split. Approve submitted, at the top, approves every submitted sheet of the week in one go, after showing you each person and their hours.' },
      { shot: 't-approvals-cards', target: 'missing', title: 'Remind the missing ones',
        text: '“Nothing filed — locks Tue 10:00” marks who is missing. Remind on their row, or Remind at the top for everyone missing, emails them from Track’s mailbox with the lock time and a link; you see who and the exact email first. A locked week asks you to unlock it before it can be filed late.' },
    ],
  },
  {
    id: 'time-expenses', area: 'time', art: 'money',
    title: 'Claim mileage and expenses',
    line: 'Journeys priced from the project’s address, receipts attached, approved by Finance.',
    go: { label: 'Open Expenses', view: 'expenses' }, url: 'time/expenses',
    steps: [
      { shot: 't-expenses', target: 'mileage', title: 'Claim mileage',
        text: 'Pick the site’s project and Track fills the distance from the office and the journey line. Change it if you went somewhere else on the way.' },
      { shot: 't-expenses', target: 'expense', title: 'Add an expense',
        text: 'A purchase with its net amount, VAT and a photo of the receipt.' },
      { shot: 't-expenses', target: 'flagged', title: 'Distances are checked',
        text: 'A claim longer than the sat-nav route is flagged, with the difference, so the approver can ask before paying it.' },
      { shot: 't-expenses', target: 'filter', title: 'Where your claims are',
        text: 'Filter to what is still awaiting approval, mileage only, or purchases. The arrows step back through earlier pay periods.' },
    ],
  },

  // ── Projects ──────────────────────────────────────────────────────────────
  {
    id: 'projects-find', area: 'projects', art: 'project',
    title: 'Find a project and see what’s late',
    line: 'The whole portfolio by stage, then the register to search and sort.',
    go: { label: 'Open Projects', view: 'projects' }, url: 'projects',
    steps: [
      { shot: 't-projects', target: 'stages', title: 'Where everything is',
        text: 'How many jobs sit at each stage, from Not Started to Complete. Status only moves forward.' },
      { shot: 't-projects-list', target: 'chips', title: 'Live, not started, complete',
        text: 'Live is the default. All shows every job on the books.' },
      { shot: 't-projects-list', target: 'search', title: 'Search',
        text: 'Type a number, a client or a few words of the description.',
        tip: 'Faster still: press [shortcut K] anywhere and type the four digits.' },
      { shot: 't-projects-list', target: 'lead', title: 'Narrow it down',
        text: 'Filter by scheme or by lead, and sort by newest, client, status or recently started.' },
      { shot: 't-projects-list', target: 'status', title: 'Change status or lead in the row',
        text: 'Click a status or a lead to change it without leaving the list. Orange status means past its deadline.' },
      { shot: 't-projects-list', target: 'row', title: 'Open a project',
        text: 'Click a row for the full record — the golden thread, instructions, documents, correspondence and money.' },
      { shot: 't-projects', target: 'meeting', title: 'Meeting mode',
        text: 'Projects aren’t added here: one starts when an enquiry is won (or, for contractors, a quote). Meeting mode runs the team meeting: one person at a time with their projects and tasks, actions typed as you talk, then No owner and Wrap up — ← and → to move on.' },
    ],
  },
  // ── Calculate ─────────────────────────────────────────────────────────────
  {
    id: 'calc-demo', area: 'calculate', who: 'calc', art: 'demo',
    title: 'Try Calculate on the demo',
    line: 'The safe place to learn: 7241 Enterprise Way and the samples, reset whenever you like.',
    go: { label: 'Open Calculate', view: 'calculate' },
    steps: [
      { shot: 'c-sandbox', target: 'sandbox', title: 'Find the sandbox',
        text: 'Below your live packages sits the Sandbox — the demo project and the sample packages. Nothing in it is filed to a real project or seen by a client.',
        tip: 'Press “Set up the demo” the first time. “Reset demo” puts every sandbox package back to how it started.' },
      { shot: 'c-sandbox', target: 'row', title: 'Open a package',
        text: 'Click any row. Each package is a single calculation: a brief, the members, the checks and, at the end, a signed PDF.' },
      { shot: 'c-home', target: 'steps', title: 'Six stages, left to right',
        text: 'Start, Design basis, Design flow, Checks, Review & sign, Issue. A tick means done; the line under each stage says what is in it. Click any stage to go there.',
        tip: 'Change something early and everything after it re-runs.' },
      { shot: 'c-home', target: 'nav', title: 'Everything in the package, down the left',
        text: 'The same stages as numbered screens, plus each calculation, frames, and the engine’s settings. The screen you are on is lit.' },
      { shot: 'c-home', target: 'continue', title: 'Or just press Continue',
        text: 'The blue button always takes you to the next thing that needs doing. If you are not sure what comes next, press it.' },
    ],
  },
  {
    id: 'calc-start', area: 'calculate', who: 'calc', art: 'new',
    title: 'Start a calc package',
    line: 'Pick the project, name the package, and let Track fill in what it already knows.',
    go: { label: 'Open Calculate', view: 'calculate' },
    steps: [
      { shot: 'c-list', target: 'new', title: 'Press “+ New package”',
        text: 'From the Calculate list — or from the Calculate button on any project, which picks the project for you.' },
      { shot: 'c-new', target: 'project', title: 'Choose the project',
        text: 'Type a number or client to filter. The package belongs to that project: its drawings, site visits and instruction are read from there.' },
      { shot: 'c-new', target: 'type', title: 'Say what kind of package it is',
        text: 'Structural for beams, joists, posts and foundations; Drainage for surface water and attenuation; Site levels for surveys, cut and fill and pipe runs.',
        tip: 'The instruction number is the one this work is billed under — usually 1.' },
      { shot: 'c-new', target: 'create', title: 'Create it',
        text: 'Track makes revision A and goes straight to the package.' },
      { shot: 'c-empty', target: 'known', title: 'See what Track already knows',
        text: 'The brief, the site visit, the drawings and the codes are pre-filled from the project. Anything marked “Filled by Track” is a proposal until you confirm it.' },
      { shot: 'c-empty', target: 'start', title: 'Confirm the design basis',
        text: 'This is always the first job. Design flow — drawing the structure — unlocks as soon as the basis is confirmed.' },
    ],
  },
  {
    id: 'calc-basis', area: 'calculate', who: 'calc', art: 'basis',
    title: 'Confirm the design basis',
    line: 'Everything the calculation assumes, stated before anything is drawn. It prints at the front.',
    go: { label: 'Open Calculate', view: 'calculate' },
    steps: [
      { shot: 'c-basis', target: 'register', title: 'One register',
        text: 'Brief, instruction, information, site visit, trees, existing structure, codes and house defaults — each with what Track read from the project.' },
      { shot: 'c-basis', target: 'row', title: '“To confirm” until you check it',
        text: 'Track’s proposals say To confirm. Once you check one it says Confirmed, with your name on it. Click any row to go straight to that question.' },
      { shot: 'c-basis', target: 'foot', title: 'Confirm one question at a time',
        text: 'Walks you through every answer in turn.' },
      { shot: 'c-basisq', target: 'list', title: 'The questions',
        text: 'Each says whether it is confirmed, filled by Track, or still needs you. Click any to jump to it.' },
      { shot: 'c-basisq', target: 'chip', title: 'Check what Track filled in',
        text: 'Leave a “Filled by Track” answer as it is and it becomes Confirmed; change it and it becomes Typed.' },
      { shot: 'c-basisq', target: 'next', title: 'Confirm and next',
        text: 'Moves to the next open question. After the last one you are back on the register.' },
      { shot: 'c-basisq', target: 'side', title: 'Set the engineer’s decisions',
        text: 'Down the right: the site visit, your assumptions and the design settings — ULS combination, timber service class, deflection limits. They print in the design basis.' },
    ],
  },
  {
    id: 'calc-structure', area: 'calculate', who: 'calc', art: 'draw',
    title: 'Draw the structure (Design flow)',
    line: 'Walls, then joists, then steels — spans come from the drawing, and every member runs as you place it.',
    go: { label: 'Open Calculate', view: 'calculate' },
    steps: [
      { shot: 'c-structure', target: 'flowtabs', title: 'Walls, then joists, then steels',
        text: 'Design flow takes the structure in the order it is built. Each step says what to place, and ticks when it is done.' },
      { shot: 'c-structure', target: 'levels', title: 'Pick the level',
        text: 'Ground, First, Roof — or + to add one. “Level below, ghosted” shows the walls and posts underneath so you can sit things on them.' },
      { shot: 'c-structure', target: 'tools', title: 'The tools for this step',
        text: 'Each has its key — B beam, C post, P point load. Esc goes back to Select; More tools has the rest.' },
      { shot: 'c-structure', target: 'canvas', title: 'Draw from support to support',
        text: 'Click where the member starts and where it ends, or type a length as you draw. A green ring means that end found something to sit on; red means it has no support yet.',
        tip: 'Autocalc is on: every change re-runs that member and everything below it straight away.' },
      { shot: 'c-structure', target: 'panel', title: 'Each member as you place it',
        text: 'Its size and its result. “Open calculation” goes to the full working.' },
      { shot: 'c-structure', target: 'foot', title: 'Keys, and moving on',
        text: 'The keys for this step are along the bottom — F follows the load from whatever is selected. Next takes you to the loads.' },
    ],
  },
  {
    id: 'calc-loads', area: 'calculate', who: 'calc', art: 'loads',
    title: 'Apply the loads',
    line: 'Build-ups with their kN/m², drawn onto the plan, traced to the ground.',
    go: { label: 'Open Calculate', view: 'calculate' },
    steps: [
      { shot: 'c-loads', target: 'library', title: 'Build-ups',
        text: 'The floors, roofs and walls in this package, each with its dead and imposed load. “+ New” starts one from a preset.' },
      { shot: 'c-loads', target: 'buildup', title: 'Layer by layer',
        text: 'Each layer has its weight; the total is worked out for you. The imposed load names its category and the clause it comes from.' },
      { shot: 'c-loads', target: 'canvas', title: 'Draw where it applies',
        text: '“Draw load areas” lets you outline where a build-up sits on the plan. The members under it pick up their share.' },
      { shot: 'c-loads', target: 'manual', title: 'Add line and point loads',
        text: 'For a partition or a padstone that is not a build-up, add it here as a line or point load with its G and Q.' },
      { shot: 'c-loads', target: 'path', title: 'Trace the load path',
        text: 'Select a joist zone, beam or column and Track shows where its load goes — roof to foundation. “Watch its load move” animates it.' },
    ],
  },
  {
    id: 'calc-flow', area: 'calculate', who: 'calc', art: 'loads',
    title: 'Watch the load move',
    line: 'Where each member’s load comes from, where it goes, and what a change would do — before you make it.',
    go: { label: 'Open Calculate', view: 'calculate' },
    steps: [
      { shot: 'c-trace', target: 'path', title: 'Trace a load path',
        text: 'From Package home’s tools, or “Trace load path” on any calculation: one member, from the roof to the ground, with what each member hands on.' },
      { shot: 'c-trace', target: 'watch', title: 'Watch it move',
        text: '“Watch its load move” opens the animated load path. (Loads has the same button for whatever you have selected.)' },
      { shot: 'c-flow', target: 'plan', title: 'The load, moving',
        text: 'Every dot is load: it falls onto the floor, runs along the joists, along the beams, down the posts and into the ground. How many dots go each way is how the load divides.' },
      { shot: 'c-flow', target: 'chips', title: 'Follow one member',
        text: 'Pick a member — or click it on the plan. Everything that loads it and everything that carries it lights up, and each hand-off is labelled with its kN.' },
      { shot: 'c-flow', target: 'casc', title: 'Floors at the top, ground at the bottom',
        text: 'The same load as a cascade: every band is as wide as the load it carries. Hover a band to see how it was worked out.',
        tip: 'Each support takes its own worst case (pattern loading), so what leaves a member need not equal what arrives.' },
      { shot: 'c-flow-story', target: 'comes', title: 'Where it comes from, where it goes',
        text: 'For the member you picked: the share each source gives it, what it passes on, and its result.' },
      { shot: 'c-flow-story', target: 'story', title: 'Play the story',
        text: 'Track tells the member’s load top to bottom, one hand-off at a time, with the arithmetic — and lights each step on the plan and the cascade.' },
      { shot: 'c-flow-try', target: 'controls', title: 'Try a change',
        text: 'Change the imposed load, a build-up, a drawn load, or the size of the member you picked. It is a preview on a copy — nothing is saved.' },
      { shot: 'c-flow-try', target: 'summary', title: 'See what it does',
        text: 'One sentence says what moved and what it means; below it, every member that works harder and every load that changed. Red is more load than before, blue less.' },
      { shot: 'c-flow-try', target: 'keep', title: 'Keep it, or reset',
        text: '“Keep this change” makes it real, and the view then shows what your last change did. [shortcut Z] undoes it like any other edit.' },
    ],
  },
  {
    id: 'calc-checks', area: 'calculate', who: 'calc', art: 'results',
    title: 'Read the results and close the flags',
    line: 'Every member, its utilisation, and anything Track thinks you have missed.',
    go: { label: 'Open Calculate', view: 'calculate' },
    steps: [
      { shot: 'c-checks', target: 'strip', title: 'Every member at a glance',
        text: 'One chip per member with its governing utilisation. The tick on each bar is 1.00 — past it, the bar turns red and the member fails.' },
      { shot: 'c-checks', target: 'review', title: 'Track reviews as you draw',
        text: 'Rules run at once; a quick look a few seconds after you stop; a full review against the standards when you leave a step. It never changes a number — it raises flags.',
        tip: 'Press . (full stop) anywhere in a package to open Track’s review list.' },
      { shot: 'c-checks', target: 'flag', title: 'A flag says what and where',
        text: 'RULE flags are fixed logic; AI flags are an opinion for you to judge. Each names the members and drawings it is about.' },
      { shot: 'c-checks', target: 'actions', title: 'Accept it, or dismiss it with a reason',
        text: '“Accept — action it”, or pick one of the reasons offered. Every flag must be closed before the package can be signed, and the reason prints on the calculation.' },
      { shot: 'c-checks', target: 'ask', title: 'Ask about this package',
        text: 'Ask Track answers from this package and the Eurocodes — “why does C1 use L_ef = 1.0L?” — and shows its sources.' },
    ],
  },
  {
    id: 'calc-member', area: 'calculate', who: 'calc', art: 'working',
    title: 'Check a member and see the working',
    line: 'Open any calculation to see every input, its source, and the sum behind each check.',
    go: { label: 'Open Calculate', view: 'calculate' },
    steps: [
      { shot: 'c-member', target: 'tabs', title: 'One tab per member',
        text: 'Every calculation in the package, with whether it passes. Click a member anywhere — the table, the chips, the plan — to land here.' },
      { shot: 'c-member', target: 'fail', title: 'If it fails, it says why',
        text: 'The governing check, the clause, and by how much. The auto-size options below are calculated fixes, not estimates.' },
      { shot: 'c-member', target: 'source', title: 'Every value shows its source',
        text: 'Each input carries a chip: ✓ from Track, ✎ typed by you, ⌖ measured from the drawing, ↳ carried from another member. Use … beside a value to override it — with a reason, which prints on the sheet.' },
      { shot: 'c-member', target: 'checks', title: 'Every check, and its working',
        text: 'The governing check is pinned at the top. “Working” under each one — or “Show working” above — opens the full derivation: formula, values, clause.' },
      { shot: 'c-member', target: 'picker', title: 'Auto-size',
        text: 'Each card is a full re-run with a different size. Pick one and the member — and everything it sits on — updates.' },
    ],
  },
  {
    id: 'calc-sign', area: 'calculate', who: 'calc', art: 'sign',
    title: 'Review, sign and issue',
    line: 'What changed, the gates that must clear, a checker and a passkey — then Issue files the PDF.',
    go: { label: 'Open Calculate', view: 'calculate' },
    steps: [
      { shot: 'c-sign', target: 'changes', title: 'What changed this revision',
        text: 'The drawings it is based on and every member added or changed — what the checker reviews.' },
      { shot: 'c-sign', target: 'gates', title: 'Every gate must clear',
        text: 'No member failing, no flag open, nothing outstanding from Track’s review. Each line says what is still missing and where to fix it.' },
      { shot: 'c-sign', target: 'send', title: 'Send for checking',
        text: 'Hands the package to the checker. They add a note for the calculation sheet and either sign with their passkey or return it to you.',
        tip: 'Once signed, the revision is read only. A change starts revision B; A stays exactly as it was signed. Issue then files the PDF to the project.' },
    ],
  },

  // ── Track ─────────────────────────────────────────────────────────────────
  {
    id: 'mail-file', area: 'mail', art: 'mail',
    title: 'File an email to a project',
    line: 'One click puts a thread on the project’s Correspondence tab for everyone.',
    go: { label: 'Open Mail', view: 'mail' },
    steps: [
      { shot: 't-mail', target: 'unfiled', title: 'Start with Unfiled',
        text: 'Unfiled holds the mail Track could not match to a project on its own. The count is how many are waiting.' },
      { shot: 't-mail', target: 'file', title: 'Click the project on the row',
        text: 'A suggested project shows on each row with why it was chosen. Click it and the thread is filed. No project? Clicking starts an enquiry instead.',
        tip: 'Filing teaches Track the sender, so their next email files itself.' },
      { shot: 't-mail', target: 'ai', title: 'Track Intelligence’s notes',
        text: 'What needs a reply today, the dates and tasks inside the email, and a suggested reply. It never sends, deletes or replies on its own.' },
      { shot: 't-mail', target: 'chips', title: 'Your labels',
        text: 'The chips are your own Gmail labels with their unread counts. Tick rows to archive, bin, snooze or star them, with Undo.' },
      { shot: 't-mail', target: 'rules', title: 'Mail rules',
        text: 'Settings › Mail rules decide where mail files automatically: auto-replies hidden, project numbers in the subject, then sender and domain rules. WhatsApp has its own settings beside it.' },
    ],
  },
  {
    id: 'files-fix', area: 'files', art: 'files',
    title: 'Fix a file name before issue',
    line: 'Track checks every name against the house numbering and says what is wrong.',
    go: { label: 'Open Files', view: 'files' },
    steps: [
      { shot: 't-files', target: 'hero', title: 'How many match',
        text: 'The figure is how many files follow PROJECT-FOLDER-DOCUMENT_REV — e.g. 8570-04-0601, then _A, _B.' },
      { shot: 't-files', target: 'fix', title: 'The name, and the right one',
        text: 'Each file that does not match shows its current name and, under it, what it should be called.' },
      { shot: 't-files', target: 'reason', title: 'Why it does not match',
        text: 'A plain reason: no project number, folder code missing, spaces in the number.' },
      { shot: 't-files', target: 'drive', title: 'Rename it in Drive',
        text: 'Track never renames, moves or deletes a file. Rename it in Drive, tick it here, and Track checks again on the next sync.' },
      { shot: 't-files', target: 'folders', title: 'Folders open in Track',
        text: 'Click a folder to browse it as a register — documents, not files — with a preview beside it.' },
    ],
  },
  {
    id: 'project-read', area: 'projects', art: 'project',
    title: 'Read a project record',
    line: 'The thread from enquiry to invoice, what is late, and what to do about it.',
    go: { label: 'Open Projects', view: 'projects' },
    steps: [
      { shot: 't-project', target: 'head', title: 'Number, client, lead, status',
        text: 'The four digits, who it is for, who leads it and where the work stands. Status moves forward only.' },
      { shot: 't-project', target: 'chain', title: 'The golden thread',
        text: 'Seven nodes: enquiry, fee, instructed, worked on, deadline, issued, invoiced. A filled node is a record that exists; a gap is one that is missing; orange is late.' },
      { shot: 't-project', target: 'attn', title: 'What needs doing',
        text: 'Track names the gap and its consequence in a sentence, with the button that fixes it.' },
      { shot: 't-project', target: 'actions', title: 'Act from the project',
        text: 'Log time, add an instruction, log an issue, book a site visit, raise an invoice — each one lands on this record. Documents, Correspondence, Tasks and Finance are the tabs below.' },
      { shot: 't-project', target: 'calculate', who: 'calc', title: 'Start calcs from here',
        text: 'For Calculate users: opens this project’s calc packages, or starts one with the project already chosen.' },
    ],
  },
  // ── Drawings ──────────────────────────────────────────────────────────────
  {
    id: 'drawing-review', area: 'files', art: 'drawing',
    title: 'Mark up a drawing',
    line: 'Cloud it, measure it, comment on it — saved for the team, and as a new PDF in Drive.',
    go: { label: 'Open Projects', view: 'projects' }, url: 'projects/8601 · Documents',
    steps: [
      { shot: 'd-sheet', target: 'mark', title: 'Open a PDF from the project',
        text: 'Any PDF on a project’s Documents tab opens here, full screen. Pinch or scroll to zoom, space-drag to pan.' },
      { shot: 'd-sheet', target: 'rail', title: 'The tools are down the left',
        text: 'Mark up, notes, drawing and measure tools, in groups. Point at one to see its name and key — P for pen, C for cloud, D for a dimension line.' },
      { shot: 'd-sheet', target: 'options', title: 'Colour, weight and dash',
        text: 'The bar above the sheet says what the tool does and holds its options. Red for comments, black for proposals, Dashed for proposed. Blue is kept for measurements.',
        tip: 'Hold ⇧ for straight lines and square boxes; ⇧-click carries the pen on from its last point.' },
      { shot: 'd-sheet', target: 'card', title: 'Cloud it and say why',
        text: 'Drag a cloud round the area to change. It is numbered, and the panel opens for the comment. Replies, the status and Assign are here too.' },
      { shot: 'd-comments', target: 'list', title: 'Every markup, numbered',
        text: 'Markups are saved as you draw and seen by anyone who opens the drawing. Filter by Open, Comments or Sketch, and click one to go to it.',
        tip: 'You can change your own words until somebody replies. After that, add a reply.' },
      { shot: 'd-sketch', target: 'mode', title: 'Sketch over',
        text: 'Fades the drawing like tracing paper, turns on Snap and Ortho, and draws in black on the Sketch layer. Layers shows or hides each layer and sets how faint the drawing is.' },
      { shot: 'd-sheet', target: 'measure', title: 'Measure',
        text: 'Length, a run, area, count and angle — in blue, so a measured number can always be told from a drawn one. Set the scale first, from the chip at the bottom.' },
      { shot: 'd-sheet', target: 'save', title: 'Save a marked-up copy',
        text: 'Save writes a new PDF next to the original in Drive, with the comments as real PDF notes. The original is never changed. Send to architect, and the greyed tools on the rail (lines, callouts, stamps, symbols, Structure), are coming soon.' },
    ],
  },

  // ── Calendar ──────────────────────────────────────────────────────────────
  {
    id: 'calendar-leave', area: 'calendar', art: 'calendar',
    title: 'Book time off and see who’s where',
    line: 'Ask for time off or a day at home, see the clash as you pick, approve the team’s.',
    go: { label: 'Open Calendar', view: 'calendar' },
    steps: [
      { shot: 't-calendar', target: 'anchor', title: 'Who’s where',
        text: 'A row per person, by office, for two or four weeks: who is off, at home or on site (with the project number). Days over the team’s limit are marked orange.' },
      { shot: 't-calendar', target: 'stats', title: 'At a glance',
        text: 'The strip above says who is in the office, at home, on site and off on the next working day.' },
      { shot: 't-calendar', target: 'today', title: 'Month',
        text: 'The month as bars by first name, your allowance, and the weeks that are clear to book: Request opens New request on those dates.' },
      { shot: 't-calendar', target: 'team', title: 'My calendar',
        text: 'Your own Google Calendar, or the shared one, as before.' },
      { shot: 't-calendar', target: 'leave', title: 'New request',
        text: 'Time off or Work from home. Pick the days on the calendar beside the form: if someone is already off, it says so straight away and offers the nearest clear dates. It goes to your approver.' },
      { shot: 't-calendar-leave', target: 'strip', title: 'Approve',
        text: 'If you approve time off, requests wait under To approve. A clash is flagged, not blocked: suggest other dates, approve anyway with a reason (kept on the record), or decline. You can’t approve your own.' },
    ],
  },

  // ── Enquiries ─────────────────────────────────────────────────────────────
  {
    id: 'enquiry-triage', area: 'enquiries', art: 'enquiry', who: 'enquiries',
    title: 'Work the enquiry pipeline',
    line: 'Every new piece of work arrives here first — read, priced, quoted, chased, decided.',
    go: { label: 'Open Enquiries', view: 'enquiries' },
    steps: [
      { shot: 't-enq', target: 'funnel', title: 'The pipeline',
        text: 'Received, read by AI, priced, quoted, chase due, decided. Orange is the step that needs you.' },
      { shot: 't-enq', target: 'chips', title: 'Open, won, lost',
        text: 'Open is what is still live. Needs Review holds the ones Track was unsure about — a web form that might be spam, say.' },
      { shot: 't-enq', target: 'fresh', title: 'Read for you',
        text: 'An enquiry email or web form becomes a row with the scope written out and a confidence bar for how sure Track is that it read it right.' },
      { shot: 't-enq', target: 'priced', title: 'Open one',
        text: 'Click a row for the enquiry: what is being asked, the fee, the client and the emails.' },
      { shot: 't-enq', target: 'add', title: 'Or add one by hand',
        text: 'For a phone call or a conversation on site.' },
    ],
  },
  {
    id: 'enquiry-fee', area: 'enquiries', art: 'enquiry', who: 'enquiries',
    title: 'Price an enquiry and send the proposal',
    line: 'Build the fee from the catalogue, check it against past jobs, then win or lose it.',
    go: { label: 'Open Enquiries', view: 'enquiries' }, url: 'enquiries/A0422',
    steps: [
      { shot: 't-enq-detail', target: 'stats', title: 'The fee, hours and confidence',
        text: 'The quoted fee, the hours it is built on, and how likely it is to be won — with the reasons Track read from the emails.' },
      { shot: 't-enq-detail', target: 'ai', title: 'Draft with AI',
        text: 'Track drafts the fee lines from the catalogue: which items, how many, how complex. It never sends a fee and never decides one — you adjust and approve.' },
      { shot: 't-enq-detail', target: 'suggest', title: 'Compare with its suggestion',
        text: 'The AI’s figure stays beside yours, with what it was unsure about. “Show the breakdown” lists its lines.' },
      { shot: 't-enq-fee', target: 'table', title: 'The fee calculator',
        text: 'Each line is a catalogue item × quantity × complexity, at the charge-out rate. Add items, commissions for subconsultants and revisions. The code does the arithmetic, never the model.' },
      { shot: 't-enq-fee', target: 'hist', title: 'Past jobs like this',
        text: 'What similar jobs were quoted and what they actually took, so you can see when a fee is light.' },
      { shot: 't-enq-proposal', target: 'issued', title: 'The proposal',
        text: 'Track writes the fee proposal from these lines. Open the PDF to check it, send it, then mark it issued — that locks the fee.' },
      { shot: 't-enq-chase', target: 'chase', title: 'Chase, then decide',
        text: 'A quote with no reply goes orange as a chase falls due. Log the chase here.' },
      { shot: 't-enq-detail', target: 'won', title: 'Won or lost',
        text: 'Mark Won to create the project — or add the work to a live one as a new instruction. Mark Lost when it goes elsewhere, so the win rate stays true.' },
    ],
  },

  // ── Clients ───────────────────────────────────────────────────────────────
  {
    id: 'clients-find', area: 'clients', art: 'clients',
    title: 'Find a client and their history',
    line: 'Projects, enquiries and billing for each client.',
    go: { label: 'Open Clients', view: 'clients' },
    steps: [
      { shot: 't-clients', target: 'search', title: 'Search',
        text: 'Name, contact or email. Prospects from open enquiries show too, tagged.' },
      { shot: 't-clients', target: 'sorts', title: 'Sort by what matters',
        text: 'A–Z, most billed, most projects, recently active, or worst thread — the clients whose records have the most gaps.' },
      { shot: 't-clients', target: 'thread', title: 'The thread',
        text: 'How complete each client’s records are, from enquiry to invoice. Click it to see where the gaps are.' },
      { shot: 't-clients', target: 'dup', title: 'Possible duplicates',
        text: 'An orange dot marks a name that looks like another. Worth merging, so one client’s work does not split across two files.' },
      { shot: 't-clients', target: 'row', title: 'Open the client',
        text: 'Click the name for their card.' },
      { shot: 't-client-card', target: 'notes', title: 'Contacts and notes',
        text: 'Contact, phone, emails, address and notes everyone sees — “prefers a call before a fee goes over £2k”.' },
      { shot: 't-client-card', target: 'projects', title: 'Their work',
        text: 'Every project, enquiry and invoice for them, below the details.' },
    ],
  },

  // ── Team ──────────────────────────────────────────────────────────────────
  {
    id: 'team-read', area: 'team', art: 'team',
    title: 'See how loaded the team is',
    line: 'Utilisation against the 90% line, who is on leave, and email signatures.',
    go: { label: 'Open Team', view: 'team' },
    steps: [
      { shot: 't-team', target: 'row', title: 'Each person',
        text: 'Role, projects they lead, utilisation over the last four weeks, leave left, and their signature. Orange is over the 90% target.' },
      { shot: 't-team', target: 'lock', title: 'Pay is locked',
        text: 'Pay, cost rates and HR records stay hidden. Unlock with Face ID, Touch ID or your device PIN — it lasts fifteen minutes.' },
      { shot: 't-team', target: 'sig', title: 'Email signatures',
        text: 'Everyone’s Gmail signature from one template. Deploy pushes it to anyone outdated; the Signature tab edits it.' },
      { shot: 't-team', target: 'add', title: 'Add someone',
        text: 'Adds a new starter. Their role decides which screens and figures they can see.' },
    ],
  },

  // ── Finance ───────────────────────────────────────────────────────────────
  {
    id: 'finance-invoices', area: 'finance', art: 'money', who: 'invoices',
    title: 'Check invoices and what is owed',
    line: 'The year so far, what is overdue, and the ledger to chase from.',
    go: { label: 'Open Invoices', view: 'invoices' },
    steps: [
      { shot: 't-summary', target: 'hero', title: 'Summary: the year',
        text: 'Invoiced this financial year (November to October), against the same point last year.' },
      { shot: 't-summary', target: 'period', title: 'This year, last year, all time',
        text: 'Switch the period. The brief and figures follow.' },
      { shot: 't-summary', target: 'toggle', title: 'Show the figures',
        text: 'Figures start hidden. “Figures” at the foot of the menu shows them — for this session only.' },
      { shot: 't-invoices', target: 'figs', title: 'Invoices: what is owed',
        text: 'This month, outstanding, overdue and paid this year. Overdue is anything past its due date.' },
      { shot: 't-invoices', target: 'status', title: 'Filter the ledger',
        text: 'By year and by status — Paid, Invoiced, Written off. Search by invoice number, client or project.' },
      { shot: 't-invoices', target: 'menu', title: 'Act on an invoice',
        text: 'The row menu opens it, downloads the PDF, sends a reminder or voids it. Payments matched in Bank show “Paid · Track”.' },
    ],
  },
  {
    id: 'finance-po', area: 'finance', art: 'money', who: 'procurement',
    title: 'Raise and approve a purchase order',
    line: 'Two people on every order: one asks, a different one approves.',
    go: { label: 'Open Purchase orders', view: 'procurement' },
    steps: [
      { shot: 't-procurement', target: 'po', title: 'Raise a PO',
        text: 'Supplier, what it is for, the job and the amount. It waits for a second Finance person or Director.' },
      { shot: 't-procurement', target: 'approve', title: 'Approve someone else’s',
        text: 'Approve or decline an order raised by someone else.' },
      { shot: 't-procurement', target: 'own', title: 'Never your own',
        text: 'An order you raised has no Approve button for you. The rule is in the database, not just the screen.' },
      { shot: 't-procurement', target: 'advance', title: 'Move it on',
        text: 'Approved → sent → fulfilled → paid. Payment is matched from Bank.' },
      { shot: 't-procurement', target: 'exp', title: 'Team expenses',
        text: 'Expense claims from timesheets are approved here too, further down, with their receipts.' },
    ],
  },
  {
    id: 'finance-bank', area: 'finance', art: 'money', who: 'bank',
    title: 'Open the bank feed',
    line: 'Read-only, behind a fresh Face ID or PIN each time.',
    go: { label: 'Open Bank', view: 'bank' },
    steps: [
      { shot: 't-bank', target: 'verify', title: 'Verify it’s you',
        text: 'Bank asks for Face ID, Touch ID or your PIN each time, checked on the server and valid for fifteen minutes.' },
      { shot: 't-bank', target: 'later', title: 'What you get',
        text: 'The accounts and transactions, read-only — Track can never move money. It suggests which invoice or PO a payment belongs to; you confirm every match.' },
    ],
  },

  // ── Ask Track ─────────────────────────────────────────────────────────────
  {
    id: 'ask-track', area: 'ask', art: 'ask',
    title: 'Find anything, ask anything',
    line: 'The navy disc on every screen: jump, search, ask, note or task.',
    url: '',
    steps: [
      { shot: 't-ask-disc', target: 'disc', title: 'The disc, bottom right',
        text: 'On every screen. Click it, or press [shortcut K].' },
      { shot: 't-ask', target: 'field', title: 'Just type',
        text: 'Four digits jumps straight to that project. Anything else searches, asks or notes — Track works out which from the words.' },
      { shot: 't-ask-find', target: 'results', title: 'Find',
        text: 'Projects, clients, enquiries, suppliers, people and screens that match. ↑↓ and Enter to open one.' },
      { shot: 't-ask', target: 'ask', title: 'Ask Track',
        text: 'A question goes to the standards library and the records, with the source it came from. Enter opens the full answer.' },
      { shot: 't-ask', target: 'note', title: 'Note',
        text: 'Write a note from anywhere and put it on a project without going there.' },
      { shot: 't-ask-task', target: 'body', title: 'Task',
        text: 'Type a task in plain words — the project, @person, day and hours are read as you type. Check the chips, then Enter.',
        tip: 'Tab switches mode if Track guessed wrong.' },
    ],
  },
];
